import { Icon, Input, Caption, Button, HStack, VStack } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { useBreakpointValue } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Pressable } from 'native-base';
import React, { useCallback, useEffect } from 'react';
import { Control, useForm } from 'react-hook-form';
import { NativeSyntheticEvent, TextInputFocusEventData } from 'react-native';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import * as zod from 'zod';

import { useRecoilQuery } from '../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../hooks/useToastCreator';
import { useCustomerActions } from '../../../store/customer/actions';
import {
  customerBySearchTermParamsState,
  customerItemState,
  customerParamsState,
  customerStatusState,
  listAllCustomersState,
  listCustomersBySearchTermState,
} from '../../../store/customer/atom';
import {
  customersAllQuery,
  customersBySearchTermQuery,
  hasCustomerItem,
} from '../../../store/customer/selector';
import { orderOptoutChannelsQuery } from '../../../store/order/selectors';

import { ResponsiveCustomerItemSelected } from './components/ItemSelected';
import { ResponsiveTable } from './components/Table';

const formSearchSchema = zod.object({
  searchTerm: zod
    .string({ required_error: 'Campo obrigatório' })
    .min(2, 'Campo deve ter no mínimo 2 caracteres')
    .refine((value) => value.trim() !== '', {
      message: 'Campo não pode consistir apenas de espaços em branco',
    }),
});

export type SearchFormData = zod.infer<typeof formSearchSchema>;

export const CustomerStep = () => {
  const hasCustomer = useRecoilValue(hasCustomerItem);

  const customerItem = useRecoilValue(customerItemState);

  const customerParams = useRecoilValue(customerParamsState);
  const customerBySearchParams = useRecoilValue(customerBySearchTermParamsState);

  const resetListCustomersBySearchTerm = useResetRecoilState(listCustomersBySearchTermState);

  const listAllCustomers = useRecoilValue(listAllCustomersState);
  const listCustomersBySearchTerm = useRecoilValue(listCustomersBySearchTermState);

  const customerStatus = useRecoilValue(customerStatusState);

  useRecoilQuery(orderOptoutChannelsQuery({ customerId: customerItem.current.customerId }));

  const {
    updateCustomerBySearchTermParams,
    updateCustomerParams,
    updateListAllCustomers,
    updateListCustomersBySearchTerm,
  } = useCustomerActions();

  const {
    data: customersAllResponse,
    loading: customersAllIsLoading,
    error: customersAllIsError,
  } = useRecoilQuery(customersAllQuery, { cachePolicy: 'no-cache' });

  const {
    data: customersBySearchTermResponse,
    loading: customersBySearchTermIsLoading,
    error: customersBySearchTermIsError,
  } = useRecoilQuery(customersBySearchTermQuery, { cachePolicy: 'no-cache' });

  const { showToast, showToastFromError } = useToastCreator('customer-toast');

  const isCustomerFirstPage = customerParams.page === 1;
  const isCustomerBySearchFirstPage = customerBySearchParams.page === 1;

  const {
    handleSubmit,
    control,
    setValue,
    resetField,
    formState: { errors, isValid: isValidSearch },
  } = useForm<SearchFormData>({
    resolver: zodResolver(formSearchSchema),
    defaultValues: { searchTerm: '' },
    mode: 'all',
  });

  const listMoreCustomers = () => {
    if (customersAllIsLoading && customerParams && customerParams.page && customerParams.page > 1) {
      return;
    }
    if (isValidSearch) {
      return;
    }

    if (
      isValidSearch &&
      !!customersBySearchTermResponse?.meta.pagination.hasNext &&
      !customersBySearchTermIsError
    ) {
      updateCustomerBySearchTermParams({ page: (customerBySearchParams.page || 0) + 1 });
    }

    if (!isValidSearch && !!customersAllResponse?.meta.pagination.hasNext && !customersAllIsError) {
      updateCustomerParams({ page: (customerParams.page || 0) + 1 });
    }
  };

  const handleSearchTerm = ({ searchTerm }: SearchFormData) => {
    resetListCustomersBySearchTerm();
    updateCustomerBySearchTermParams({ searchTerm, page: 1, skipEndpointCall: false });
  };

  const handleClearSearch = () => {
    resetListCustomersBySearchTerm();

    setValue('searchTerm', '');
    resetField('searchTerm');
  };

  const handleIsClear = useCallback((e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    e.nativeEvent.text === '' && handleClearSearch();
  }, []);

  const isLoading =
    (customersAllIsLoading && isCustomerFirstPage) ||
    (customersBySearchTermIsLoading && isCustomerBySearchFirstPage);

  const isLoadingMore =
    (customersAllIsLoading && !isCustomerFirstPage) ||
    (customersBySearchTermIsLoading && !isCustomerBySearchFirstPage);

  const isDisabledSearchInput = isLoading || isLoadingMore;

  useEffect(() => {
    if (!customerItem.previous || Object.keys(customerItem.previous)?.length === 0) {
      return;
    }
    if (customerItem.previous.accountNumber !== customerItem.current.accountNumber) {
      showToast('success', 'Tudo certo!', 'Seu cliente foi alterado com sucesso!', {
        duration: 3000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerItem.previous]);

  useEffect(() => {
    if (customersAllIsError) {
      if (instanceOfErrorListResponse(customersAllIsError)) {
        (customersAllIsError as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
          showToastFromError(errorItem)
        );
      }
    }
  }, [customersAllIsError]);

  useEffect(() => {
    if (customersBySearchTermIsError) {
      if (instanceOfErrorListResponse(customersBySearchTermIsError)) {
        (customersBySearchTermIsError as unknown as ErrorListResponse).errors?.forEach(
          (errorItem) => showToastFromError(errorItem)
        );
      }
    }
  }, [customersBySearchTermIsError]);

  useEffect(() => {
    const items = customersAllResponse?.data;

    if (!customersAllIsLoading && items?.length) {
      updateListAllCustomers(
        customersAllResponse?.meta?.pagination?.page === 1 ? items : [...listAllCustomers, ...items]
      );
    }
  }, [customersAllIsLoading, customersAllResponse]);

  useEffect(() => {
    const items = customersBySearchTermResponse?.data;

    if (!customersBySearchTermIsLoading && items?.length) {
      updateListCustomersBySearchTerm(
        customersBySearchTermResponse?.meta?.pagination.page === 1
          ? items
          : [...listCustomersBySearchTerm, ...items]
      );
    }
  }, [customersBySearchTermIsLoading, customersBySearchTermResponse]);

  const customerStepDesktopAndTablet = (
    <Box>
      {hasCustomer &&
        (customerStatus.status === 'opened' || customerStatus.status === 'completed') && (
          <ResponsiveCustomerItemSelected />
        )}
      {customerStatus.status !== 'completed' && (
        <VStack pt={Spacing['inline.xxs']}>
          <HStack
            width={{ base: '35%', sm: '44%', lg: '35%' }}
            justifyContent={'space-between'}
            space={Spacing['inline.xxxs']}
            mb={'8px'}
            px={Spacing['inline.xxs']}>
            <Input
              testId="qa"
              noValidate={true}
              onSubmitEditing={handleSubmit(handleSearchTerm)}
              onBlur={handleIsClear}
              name="searchTerm"
              control={control as unknown as Control}
              error={errors.searchTerm}
              isDisabled={isDisabledSearchInput}
              variant="rounded"
              autoComplete="off"
              inputRightElement={
                <Pressable
                  onPress={isValidSearch ? handleClearSearch : null}
                  pr={isValidSearch ? 3 : 4}
                  isDisabled={isDisabledSearchInput}>
                  <Icon testId="qa" name={isValidSearch ? 'Close' : 'Search'} size="xxxs" />
                </Pressable>
              }
              placeholder="Pesquise por nome, conta, CPF ou CNPJ"
            />
            <Button
              testID="qa"
              variant="primary"
              onPress={handleSubmit(handleSearchTerm)}
              isDisabled={!isValidSearch || isDisabledSearchInput}>
              Buscar
            </Button>
          </HStack>

          <HStack
            alignItems={'center'}
            space={'22px'}
            px={Spacing['inline.xxs']}
            my={Spacing['inline.xxxs']}
            testID="legend-container">
            <Caption
              testId="qa"
              icon="QualifiedClient"
              iconSize="xs"
              text="Investidor qualificado"
              iconColor={Color['brand.secondary.principal']}
            />
            <Caption
              testId="qa"
              icon="ProfessionalInvestor"
              text="Investidor profissional"
              iconSize="xs"
              iconColor={Color['brand.tertiary.principal']}
            />
          </HStack>
          <ResponsiveTable
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            handleEndReached={listMoreCustomers}
            ListAll={listAllCustomers}
            ListBySearchTerm={listCustomersBySearchTerm}
            handleClearSearch={handleClearSearch}
            hasEmpty={!!customersAllResponse?.data?.length}
          />
        </VStack>
      )}
    </Box>
  );

  const customerStep = useBreakpointValue({
    base: (
      <Box>
        {hasCustomer &&
          (customerStatus.status === 'completed' || customerStatus.status === 'opened') && (
            <ResponsiveCustomerItemSelected />
          )}
        {customerStatus.status !== 'completed' && (
          <VStack>
            <Box
              justifyContent={'space-between'}
              py={Spacing['inline.xxs']}
              px={Spacing['inline.xxxs']}>
              <Input
                testId="qa"
                noValidate={true}
                onSubmitEditing={handleSubmit(handleSearchTerm)}
                onBlur={handleIsClear}
                name="searchTerm"
                control={control as unknown as Control}
                error={errors.searchTerm}
                isDisabled={isDisabledSearchInput}
                variant="rounded"
                autoComplete="off"
                inputRightElement={
                  <Pressable
                    onPress={isValidSearch ? handleClearSearch : null}
                    pr={isValidSearch ? 3 : 4}
                    isDisabled={isDisabledSearchInput}>
                    <Icon testId="qa" name={isValidSearch ? 'Close' : 'Search'} size="xxxs" />
                  </Pressable>
                }
                placeholder="Pesquise por nome, conta, CPF ou CNPJ"
              />
            </Box>
            <ResponsiveTable
              isLoading={isLoading}
              isLoadingMore={isLoadingMore}
              handleEndReached={listMoreCustomers}
              ListAll={listAllCustomers}
              ListBySearchTerm={listCustomersBySearchTerm}
              handleClearSearch={handleClearSearch}
              hasEmpty={!!customersAllResponse?.data?.length}
            />
          </VStack>
        )}
      </Box>
    ),
    sm: customerStepDesktopAndTablet,
  });

  return customerStep;
};
